// AIAG-VDA PFMEA Auditor Utility
// ============================================================
// Evaluates PFMEA data against actual AIAG-VDA audit requirements
// Based on AIAG-VDA PFMEA Reference Manual (1st Edition, 2019)
// and PPAP/APQP requirements for automotive submission
// ============================================================

import {
    PfmeaItem,
    ProcessFlowDiagram,
    ControlPlan,
    ProjectScoping,
    ProcessFunction,
    ProductRequirement,
} from '../types';

// ============================================================
// AUDIT SEVERITY LEVELS
// ============================================================

export type AuditSeverity = 'critical' | 'major' | 'minor' | 'observation' | 'pass';

export interface AuditFinding {
    id: string;
    step: number;                    // AIAG-VDA Step 1-7
    category: string;                // e.g., "5T Framework", "Structure Tree", "Function Net"
    requirement: string;             // What AIAG-VDA requires
    finding: string;                 // What was found/missing
    severity: AuditSeverity;
    auditorTip: string;              // What auditors specifically look for
    ppapImpact?: string;             // Impact on PPAP submission
    recommendation: string;          // How to fix
    referenceSection?: string;       // AIAG-VDA manual section
}

export interface StepAuditResult {
    step: number;
    stepName: string;
    score: number;                   // 0-100
    status: 'not-started' | 'in-progress' | 'compliant' | 'non-conformance';
    findings: AuditFinding[];
    requiredItems: number;
    completedItems: number;
    auditorNotes: string[];
}

export interface AuditReport {
    overallScore: number;            // 0-100 weighted score
    ppapReadiness: number;           // 0-100 for PPAP submission
    status: 'ready' | 'needs-work' | 'not-ready';
    stepResults: StepAuditResult[];
    criticalFindings: AuditFinding[];
    majorFindings: AuditFinding[];
    minorFindings: AuditFinding[];
    ppapGaps: string[];              // Missing PPAP elements
    recommendations: string[];
    generatedAt: Date;
}

// ============================================================
// STRICT AUDITOR STATUS CALCULATION
// ============================================================
// VDA/IATF Auditors use this logic:
// - ANY Critical Finding = 'non-conformance' (automatic failure)
// - ANY Major Finding = Cannot be 'compliant' (must be 'in-progress' at best)
// - Score >= 90 AND no Critical/Major = 'compliant'
// ============================================================

function calculateStrictStatus(
    score: number,
    findings: AuditFinding[]
): 'compliant' | 'in-progress' | 'not-started' | 'non-conformance' {
    const hasCritical = findings.some(f => f.severity === 'critical');
    const hasMajor = findings.some(f => f.severity === 'major');

    // Critical finding = Automatic non-conformance (Red)
    if (hasCritical) {
        return 'non-conformance';
    }

    // Major finding = Cannot be compliant, but work is in progress
    if (hasMajor) {
        return score > 0 ? 'in-progress' : 'not-started';
    }

    // No Critical/Major: Use score threshold
    if (score >= 90) {
        return 'compliant';
    } else if (score > 0) {
        return 'in-progress';
    }

    return 'not-started';
}

// ============================================================
// AIAG-VDA STEP 1: PLANNING & PREPARATION AUDIT
// ============================================================

function auditStep1Planning(
    scoping: ProjectScoping | undefined,
    projectName: string | null
): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 5; // 5T Framework
    let completedItems = 0;

    // Check if project is defined
    if (!projectName) {
        findings.push({
            id: 'S1-001',
            step: 1,
            category: 'Project Definition',
            requirement: 'AIAG-VDA requires a defined project scope with clear boundaries',
            finding: 'No project name defined',
            severity: 'critical',
            auditorTip: 'Auditors verify project scope is documented BEFORE analysis begins. Missing scope = audit failure.',
            ppapImpact: 'PPAP Part Submission Warrant requires part name/number',
            recommendation: 'Define project name, part number, and customer information',
            referenceSection: 'AIAG-VDA 2.2.1',
        });
    } else {
        completedItems++;
    }

    // 5T Framework Checks
    if (!scoping) {
        findings.push({
            id: 'S1-002',
            step: 1,
            category: '5T Framework',
            requirement: 'AIAG-VDA Step 1 requires 5T Framework: InTent, Timing, Team, Tools, Tasks',
            finding: 'No 5T scoping document found',
            severity: 'critical',
            auditorTip: 'Auditors ask: "Show me your FMEA project plan." Without 5T, you cannot demonstrate planning.',
            ppapImpact: 'APQP Phase 1 requires project planning deliverables',
            recommendation: 'Complete the 5T scoping document in Project Scoping page',
            referenceSection: 'AIAG-VDA 2.2',
        });
    } else {
        // Check InTent
        if (!scoping.intent?.goals?.length || !scoping.intent?.scopeBoundaries) {
            findings.push({
                id: 'S1-003',
                step: 1,
                category: '5T - InTent',
                requirement: 'InTent must define goals, scope boundaries, and assumptions',
                finding: 'InTent section incomplete - missing goals or scope boundaries',
                severity: 'major',
                auditorTip: 'Auditors verify: What is in scope? What is excluded? Vague scope = scope creep = incomplete FMEA.',
                recommendation: 'Define specific goals, scope boundaries, assumptions, and exclusions',
                referenceSection: 'AIAG-VDA 2.2.2',
            });
        } else {
            completedItems++;
        }

        // Check Timing
        if (!scoping.timing?.kickoffDate || !scoping.timing?.targetCompletionDate) {
            findings.push({
                id: 'S1-004',
                step: 1,
                category: '5T - Timing',
                requirement: 'Timing must include kickoff date, milestones, and target completion',
                finding: 'Timing section incomplete - no project schedule defined',
                severity: 'major',
                auditorTip: 'Auditors check: Does FMEA timeline align with APQP gates? Late FMEA = late PPAP.',
                ppapImpact: 'FMEA must be complete before PPAP Phase 4 submission',
                recommendation: 'Define kickoff date, milestones aligned to APQP phases, and completion date',
                referenceSection: 'AIAG-VDA 2.2.3',
            });
        } else {
            completedItems++;
        }

        // Check Team
        if (!scoping.team?.lead || !scoping.team?.members?.length) {
            findings.push({
                id: 'S1-005',
                step: 1,
                category: '5T - Team',
                requirement: 'Team must be cross-functional with defined roles and expertise',
                finding: 'Team section incomplete - no team lead or members defined',
                severity: 'major',
                auditorTip: 'Auditors ask: "Who participated in the FMEA?" Single-person FMEA is a red flag.',
                recommendation: 'Define team lead and cross-functional members (Quality, Manufacturing, Design, etc.)',
                referenceSection: 'AIAG-VDA 2.2.4',
            });
        } else {
            if (scoping.team.members.length < 3) {
                findings.push({
                    id: 'S1-006',
                    step: 1,
                    category: '5T - Team',
                    requirement: 'FMEA team should be cross-functional with multiple disciplines',
                    finding: `Only ${scoping.team.members.length} team members defined`,
                    severity: 'minor',
                    auditorTip: 'Best practice: Include Design, Manufacturing, Quality, Supplier, and Customer representatives.',
                    recommendation: 'Add team members from different functional areas',
                    referenceSection: 'AIAG-VDA 2.2.4',
                });
            }
            completedItems++;
        }

        // Check Tools
        if (!scoping.tools?.software?.length && !scoping.tools?.standards?.length) {
            findings.push({
                id: 'S1-007',
                step: 1,
                category: '5T - Tools',
                requirement: 'Tools section must document software, templates, and reference standards',
                finding: 'No tools or standards documented',
                severity: 'minor',
                auditorTip: 'Auditors check: What standard are you using? AIAG-VDA? AIAG 4th Ed? ISO-14971?',
                recommendation: 'Document FMEA software, templates, and applicable standards',
                referenceSection: 'AIAG-VDA 2.2.5',
            });
        } else {
            completedItems++;
        }

        // Check Tasks
        if (!scoping.tasks?.length) {
            findings.push({
                id: 'S1-008',
                step: 1,
                category: '5T - Tasks',
                requirement: 'Tasks section must define work breakdown with owners and due dates',
                finding: 'No tasks defined for FMEA completion',
                severity: 'minor',
                auditorTip: 'Auditors verify task completion dates align with APQP milestones.',
                recommendation: 'Create task list with owners, due dates, and dependencies',
                referenceSection: 'AIAG-VDA 2.2.6',
            });
        } else {
            completedItems++;
        }
    }

    auditorNotes.push('Step 1 establishes the foundation. Without proper planning, the entire FMEA is at risk.');
    auditorNotes.push('PPAP requires evidence of project planning (APQP Phase 1 deliverables).');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 1,
        stepName: 'Planning & Preparation',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// AIAG-VDA STEP 2: STRUCTURE ANALYSIS AUDIT
// ============================================================

function auditStep2Structure(
    processFlow: ProcessFlowDiagram | null,
    pfmeaItems: PfmeaItem[]
): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 4;
    let completedItems = 0;

    // Check Process Flow Diagram exists
    if (!processFlow || !processFlow.steps?.length) {
        findings.push({
            id: 'S2-001',
            step: 2,
            category: 'Process Flow Diagram',
            requirement: 'AIAG-VDA requires a Process Flow Diagram (PFD) showing all operations',
            finding: 'No Process Flow Diagram defined or no process steps exist',
            severity: 'critical',
            auditorTip: 'Auditors will ask for your PFD immediately. No PFD = No PFMEA = PPAP rejection.',
            ppapImpact: 'PFD is a required PPAP element (Element #4)',
            recommendation: 'Create Process Flow Diagram with all manufacturing operations',
            referenceSection: 'AIAG-VDA 2.3',
        });
    } else {
        completedItems++;

        // Check for proper step numbering
        const hasStepNumbers = processFlow.steps.every(s => s.stepNumber);
        if (!hasStepNumbers) {
            findings.push({
                id: 'S2-002',
                step: 2,
                category: 'Process Flow Diagram',
                requirement: 'Each process step must have a unique operation number',
                finding: 'Some process steps are missing operation numbers',
                severity: 'major',
                auditorTip: 'Auditors verify: Does PFD operation number match PFMEA? Match Control Plan?',
                recommendation: 'Assign unique operation numbers (e.g., OP-010, OP-020) to each step',
                referenceSection: 'AIAG-VDA 2.3.1',
            });
        } else {
            completedItems++;
        }

        // Check Structure Tree (Process Item → Process Step → Work Element)
        const hasWorkElements = processFlow.steps.some(s => s.workElementIds?.length);
        if (!hasWorkElements) {
            findings.push({
                id: 'S2-003',
                step: 2,
                category: 'Structure Tree',
                requirement: 'AIAG-VDA requires 3-level hierarchy: Process Item → Process Step → Work Element',
                finding: 'No Work Elements defined - Structure Tree is incomplete',
                severity: 'major',
                auditorTip: 'Auditors look for granular breakdown. Without Work Elements, failure modes may be missed.',
                recommendation: 'Define Work Elements for each Process Step (e.g., "Position part", "Activate weld cycle")',
                referenceSection: 'AIAG-VDA 2.3.2',
            });
        } else {
            completedItems++;
        }

        // Check if process steps have descriptions
        const stepsWithDescriptions = processFlow.steps.filter(s => s.description).length;
        if (stepsWithDescriptions < processFlow.steps.length) {
            findings.push({
                id: 'S2-004',
                step: 2,
                category: 'Process Step Documentation',
                requirement: 'Each process step should have a clear description of the operation',
                finding: `${processFlow.steps.length - stepsWithDescriptions} process steps missing descriptions`,
                severity: 'minor',
                auditorTip: 'Clear descriptions help ensure all failure modes are identified.',
                recommendation: 'Add descriptions explaining what each process step does',
                referenceSection: 'AIAG-VDA 2.3.1',
            });
        } else {
            completedItems++;
        }
    }

    // Check PFMEA coverage of process steps
    if (processFlow?.steps?.length && pfmeaItems.length) {
        const pfmeaStepNumbers = new Set(pfmeaItems.map(i => i.processStepNumber));
        const pfdStepNumbers = processFlow.steps.map(s => s.stepNumber);
        const uncoveredSteps = pfdStepNumbers.filter(sn => !pfmeaStepNumbers.has(sn));

        if (uncoveredSteps.length > 0) {
            findings.push({
                id: 'S2-005',
                step: 2,
                category: 'PFMEA Coverage',
                requirement: 'Every process step in PFD should have at least one PFMEA item',
                finding: `${uncoveredSteps.length} process steps have no PFMEA analysis: ${uncoveredSteps.join(', ')}`,
                severity: 'major',
                auditorTip: 'Auditors compare PFD to PFMEA. Missing steps = incomplete analysis.',
                ppapImpact: 'All operations must be analyzed for PPAP approval',
                recommendation: 'Add PFMEA items for each uncovered process step',
                referenceSection: 'AIAG-VDA 2.3.3',
            });
        }
    }

    auditorNotes.push('Process Flow is the backbone of PFMEA. Every operation must be captured.');
    auditorNotes.push('PPAP requires PFD to match PFMEA operation numbers exactly.');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 2,
        stepName: 'Structure Analysis',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// AIAG-VDA STEP 3: FUNCTION ANALYSIS AUDIT
// ============================================================

function auditStep3Function(
    _processFlow: ProcessFlowDiagram | null,
    pfmeaItems: PfmeaItem[],
    functions: ProcessFunction[] = [],
    requirements: ProductRequirement[] = []
): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 4;
    let completedItems = 0;

    // Check if functions are defined
    if (functions.length === 0) {
        // Check if PFMEA items have processFunction field populated
        const itemsWithFunctions = pfmeaItems.filter(i => i.processFunction && i.processFunction.trim() !== '');

        if (itemsWithFunctions.length === 0) {
            findings.push({
                id: 'S3-001',
                step: 3,
                category: 'Function Definition',
                requirement: 'AIAG-VDA requires Process Functions to be defined for each process step',
                finding: 'No process functions defined - Step 3 not started',
                severity: 'critical',
                auditorTip: 'Auditors ask: "What is the function of this operation?" No function = no failure mode basis.',
                recommendation: 'Define the function (purpose) of each process step before identifying failures',
                referenceSection: 'AIAG-VDA 2.4',
            });
        } else {
            completedItems++;
            if (itemsWithFunctions.length < pfmeaItems.length) {
                findings.push({
                    id: 'S3-002',
                    step: 3,
                    category: 'Function Definition',
                    requirement: 'Every PFMEA item should have a defined process function',
                    finding: `${pfmeaItems.length - itemsWithFunctions.length} PFMEA items missing process function`,
                    severity: 'major',
                    auditorTip: 'Function is the basis for failure mode. "Loss of function", "Degraded function", etc.',
                    recommendation: 'Add process function to all PFMEA items',
                    referenceSection: 'AIAG-VDA 2.4.1',
                });
            } else {
                completedItems++;
            }
        }
    } else {
        completedItems += 2;
    }

    // Check for Function Net (interconnected functions)
    if (functions.length > 0) {
        const functionsWithConnections = functions.filter(f =>
            (f.inputFunctions?.length || 0) + (f.outputFunctions?.length || 0) > 0
        );
        if (functionsWithConnections.length === 0) {
            findings.push({
                id: 'S3-003',
                step: 3,
                category: 'Function Net',
                requirement: 'AIAG-VDA recommends Function Net showing input/output relationships',
                finding: 'No Function Net connections defined between process functions',
                severity: 'minor',
                auditorTip: 'Function Net helps trace how failures propagate. Recommended but not required.',
                recommendation: 'Define input and output relationships between functions',
                referenceSection: 'AIAG-VDA 2.4.2',
            });
        } else {
            completedItems++;
        }
    }

    // Check for Requirements linkage
    if (requirements.length === 0) {
        findings.push({
            id: 'S3-004',
            step: 3,
            category: 'Requirements',
            requirement: 'Process functions should trace to product/customer requirements',
            finding: 'No product requirements defined for traceability',
            severity: 'minor',
            auditorTip: 'Traceability to requirements demonstrates the FMEA addresses what matters.',
            ppapImpact: 'Customer requirements must be addressed in PPAP documentation',
            recommendation: 'Define product requirements and link them to process functions',
            referenceSection: 'AIAG-VDA 2.4.3',
        });
    } else {
        completedItems++;
    }

    // Check if PFMEA items have requirement field populated
    const itemsWithRequirements = pfmeaItems.filter(i => i.requirement && i.requirement.trim() !== '');
    if (itemsWithRequirements.length < pfmeaItems.length * 0.5) {
        findings.push({
            id: 'S3-005',
            step: 3,
            category: 'Requirements Linkage',
            requirement: 'PFMEA items should link to specific product requirements',
            finding: `Only ${itemsWithRequirements.length} of ${pfmeaItems.length} items have requirements linked`,
            severity: 'observation',
            auditorTip: 'Best practice: Link each failure mode to the requirement it could violate.',
            recommendation: 'Add requirement references to PFMEA items',
            referenceSection: 'AIAG-VDA 2.4.3',
        });
    }

    auditorNotes.push('Function is the foundation. Failure Mode = Loss/Degradation of Function.');
    auditorNotes.push('Strong function definitions make failure analysis systematic, not guesswork.');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 3,
        stepName: 'Function Analysis',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// AIAG-VDA STEP 4: FAILURE ANALYSIS AUDIT
// ============================================================

function auditStep4Failure(pfmeaItems: PfmeaItem[]): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 5;
    let completedItems = 0;

    if (pfmeaItems.length === 0) {
        findings.push({
            id: 'S4-001',
            step: 4,
            category: 'PFMEA Items',
            requirement: 'AIAG-VDA requires failure modes to be identified for each process step',
            finding: 'No PFMEA items exist - Failure Analysis not started',
            severity: 'critical',
            auditorTip: 'No failure analysis = No PFMEA = Automatic PPAP rejection.',
            ppapImpact: 'PFMEA is a required PPAP element (Element #7)',
            recommendation: 'Create PFMEA items using AI Ideation or manual entry',
            referenceSection: 'AIAG-VDA 2.5',
        });
        return {
            step: 4,
            stepName: 'Failure Analysis',
            score: 0,
            status: 'not-started',
            findings,
            requiredItems,
            completedItems,
            auditorNotes,
        };
    }

    completedItems++; // Has PFMEA items

    // Check Failure Mode Format
    const itemsWithFailureMode = pfmeaItems.filter(i => i.failureMode && i.failureMode.trim() !== '');
    if (itemsWithFailureMode.length < pfmeaItems.length) {
        findings.push({
            id: 'S4-002',
            step: 4,
            category: 'Failure Mode',
            requirement: 'Every PFMEA item must have a clearly defined failure mode',
            finding: `${pfmeaItems.length - itemsWithFailureMode.length} items missing failure mode`,
            severity: 'critical',
            auditorTip: 'Failure mode should describe HOW the process could fail (verb-noun format).',
            recommendation: 'Add failure mode in format: "[What] [How it fails]" (e.g., "Hole diameter undersized")',
            referenceSection: 'AIAG-VDA 2.5.1',
        });
    } else {
        completedItems++;
    }

    // Check Failure Effect Chain (Local → Next → End User)
    const itemsWithCompleteEffectChain = pfmeaItems.filter(i =>
        i.failureEffectEnd && i.failureEffectEnd.trim() !== ''
    );
    const itemsWithFullChain = pfmeaItems.filter(i =>
        i.failureEffectLocal && i.failureEffectNext && i.failureEffectEnd
    );

    if (itemsWithCompleteEffectChain.length < pfmeaItems.length) {
        findings.push({
            id: 'S4-003',
            step: 4,
            category: 'Failure Effect Chain',
            requirement: 'AIAG-VDA requires 3-level effect chain: Local → Next → End User',
            finding: `${pfmeaItems.length - itemsWithCompleteEffectChain.length} items missing end user effect`,
            severity: 'major',
            auditorTip: 'Auditors check: What happens at this station (Local)? At next station (Next)? To end customer (End)?',
            recommendation: 'Complete the failure effect chain for all items',
            referenceSection: 'AIAG-VDA 2.5.2',
        });
    } else {
        completedItems++;
    }

    if (itemsWithFullChain.length < pfmeaItems.length * 0.8) {
        findings.push({
            id: 'S4-004',
            step: 4,
            category: 'Failure Effect Chain',
            requirement: 'Full effect chain (Local, Next, End) provides complete impact understanding',
            finding: `Only ${itemsWithFullChain.length} of ${pfmeaItems.length} items have complete Local→Next→End chain`,
            severity: 'minor',
            auditorTip: 'Complete chains help assign proper Severity ratings.',
            recommendation: 'Add Local and Next Level effects where missing',
            referenceSection: 'AIAG-VDA 2.5.2',
        });
    }

    // Check Failure Cause
    const itemsWithCause = pfmeaItems.filter(i => i.failureCause && i.failureCause.trim() !== '');
    if (itemsWithCause.length < pfmeaItems.length) {
        findings.push({
            id: 'S4-005',
            step: 4,
            category: 'Failure Cause',
            requirement: 'Every failure mode must have at least one root cause identified',
            finding: `${pfmeaItems.length - itemsWithCause.length} items missing failure cause`,
            severity: 'major',
            auditorTip: 'Causes should be specific and actionable. "Operator error" is too vague.',
            recommendation: 'Add specific root causes using 4M/5M categories',
            referenceSection: 'AIAG-VDA 2.5.3',
        });
    } else {
        completedItems++;
    }

    // Check 4M Classification
    const itemsWith4M = pfmeaItems.filter(i =>
        i.processElement && ['Man', 'Machine', 'Material', 'Method'].includes(i.processElement as string)
    );
    if (itemsWith4M.length < pfmeaItems.length) {
        findings.push({
            id: 'S4-006',
            step: 4,
            category: '4M Classification',
            requirement: 'Causes should be classified by 4M category (Man, Machine, Material, Method)',
            finding: `${pfmeaItems.length - itemsWith4M.length} items not classified by 4M`,
            severity: 'minor',
            auditorTip: '4M classification helps identify systemic issues and assign responsibilities.',
            recommendation: 'Assign 4M category to each PFMEA item',
            referenceSection: 'AIAG-VDA 2.5.3',
        });
    } else {
        completedItems++;
    }

    auditorNotes.push('Failure Analysis is the heart of PFMEA. Quality here determines overall value.');
    auditorNotes.push('Auditors look for: Specific failure modes, complete effect chains, actionable causes.');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 4,
        stepName: 'Failure Analysis',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// AIAG-VDA STEP 5: RISK ANALYSIS AUDIT
// ============================================================

function auditStep5Risk(pfmeaItems: PfmeaItem[]): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 5;
    let completedItems = 0;

    if (pfmeaItems.length === 0) {
        return {
            step: 5,
            stepName: 'Risk Analysis',
            score: 0,
            status: 'not-started',
            findings: [{
                id: 'S5-001',
                step: 5,
                category: 'Risk Ratings',
                requirement: 'PFMEA items must exist before risk analysis',
                finding: 'No PFMEA items to rate',
                severity: 'critical',
                auditorTip: 'Complete Step 4 (Failure Analysis) first.',
                recommendation: 'Create PFMEA items before risk rating',
                referenceSection: 'AIAG-VDA 2.6',
            }],
            requiredItems,
            completedItems,
            auditorNotes,
        };
    }

    // Check Severity ratings
    const itemsWithSeverity = pfmeaItems.filter(i => i.severity >= 1 && i.severity <= 10);
    if (itemsWithSeverity.length < pfmeaItems.length) {
        findings.push({
            id: 'S5-002',
            step: 5,
            category: 'Severity Rating',
            requirement: 'All items must have Severity rating (1-10)',
            finding: `${pfmeaItems.length - itemsWithSeverity.length} items missing Severity rating`,
            severity: 'critical',
            auditorTip: 'Severity is based on END USER effect. Safety/Regulatory = 9-10.',
            recommendation: 'Assign Severity based on effect severity table',
            referenceSection: 'AIAG-VDA 2.6.1',
        });
    } else {
        completedItems++;
    }

    // Check Occurrence ratings
    const itemsWithOccurrence = pfmeaItems.filter(i => i.occurrence >= 1 && i.occurrence <= 10);
    if (itemsWithOccurrence.length < pfmeaItems.length) {
        findings.push({
            id: 'S5-003',
            step: 5,
            category: 'Occurrence Rating',
            requirement: 'All items must have Occurrence rating (1-10)',
            finding: `${pfmeaItems.length - itemsWithOccurrence.length} items missing Occurrence rating`,
            severity: 'critical',
            auditorTip: 'Occurrence is based on PREVENTION control effectiveness. How likely is the cause?',
            recommendation: 'Assign Occurrence based on prevention control effectiveness',
            referenceSection: 'AIAG-VDA 2.6.2',
        });
    } else {
        completedItems++;
    }

    // Check Detection ratings
    const itemsWithDetection = pfmeaItems.filter(i => i.detection >= 1 && i.detection <= 10);
    if (itemsWithDetection.length < pfmeaItems.length) {
        findings.push({
            id: 'S5-004',
            step: 5,
            category: 'Detection Rating',
            requirement: 'All items must have Detection rating (1-10)',
            finding: `${pfmeaItems.length - itemsWithDetection.length} items missing Detection rating`,
            severity: 'critical',
            auditorTip: 'Detection is based on DETECTION control effectiveness. How likely to catch before shipping?',
            recommendation: 'Assign Detection based on detection control effectiveness',
            referenceSection: 'AIAG-VDA 2.6.3',
        });
    } else {
        completedItems++;
    }

    // Check Controls for High Priority Items (Strict Check)
    const highApItems = pfmeaItems.filter(i => i.actionPriority === 'H');
    const highApMissingControls = highApItems.filter(i =>
        (!i.preventionControls || i.preventionControls.length === 0) ||
        (!i.detectionControls || i.detectionControls.length === 0)
    );

    if (highApMissingControls.length > 0) {
        findings.push({
            id: 'S5-SPEC-001',
            step: 5,
            category: 'High Risk Controls',
            requirement: 'High Action Priority (AP=H) items MUST have documented Prevention AND Detection controls',
            finding: `${highApMissingControls.length} High AP items are missing controls`,
            severity: 'critical',
            auditorTip: 'AUDITOR NOTE: You cannot claim a rating that results in AP=H without documenting the current controls.',
            ppapImpact: 'Rejected. High risk process steps must have defined controls.',
            recommendation: 'Document specific Prevention and Detection controls for all High AP items',
            referenceSection: 'AIAG-VDA 2.6.4',
        });
    }

    // Check Prevention Controls (General Coverage)
    const itemsWithPrevention = pfmeaItems.filter(i => i.preventionControls?.length > 0);
    if (itemsWithPrevention.length < pfmeaItems.length * 0.9) {
        findings.push({
            id: 'S5-005',
            step: 5,
            category: 'Prevention Controls',
            requirement: 'Prevention controls must be documented to justify Occurrence rating',
            finding: `${pfmeaItems.length - itemsWithPrevention.length} items have no prevention controls`,
            severity: 'major',
            auditorTip: 'Auditors ask: "What prevents this failure?" No answer = O rating not justified.',
            recommendation: 'Document prevention controls for each failure mode',
            referenceSection: 'AIAG-VDA 2.6.4',
        });
    } else {
        completedItems++;
    }

    // Check Detection Controls (General Coverage)
    const itemsWithDetectionControls = pfmeaItems.filter(i => i.detectionControls?.length > 0);
    if (itemsWithDetectionControls.length < pfmeaItems.length * 0.9) {
        findings.push({
            id: 'S5-006',
            step: 5,
            category: 'Detection Controls',
            requirement: 'Detection controls must be documented to justify Detection rating',
            finding: `${pfmeaItems.length - itemsWithDetectionControls.length} items have no detection controls`,
            severity: 'major',
            auditorTip: 'Auditors ask: "How do you detect this before it ships?" No answer = D rating not justified.',
            recommendation: 'Document detection controls for each failure mode',
            referenceSection: 'AIAG-VDA 2.6.5',
        });
    } else {
        completedItems++;
    }

    // Check Action Priority Logic
    const itemsWithAP = pfmeaItems.filter(i => i.actionPriority);
    if (itemsWithAP.length < pfmeaItems.length) {
        findings.push({
            id: 'S5-007',
            step: 5,
            category: 'Action Priority',
            requirement: 'AIAG-VDA uses AP (H/M/L) instead of RPN for action prioritization',
            finding: `${pfmeaItems.length - itemsWithAP.length} items missing Action Priority`,
            severity: 'major',
            auditorTip: 'AIAG-VDA 2019 uses AP (High/Medium/Low), not RPN. Using RPN alone is outdated.',
            recommendation: 'Calculate Action Priority using AP logic table',
            referenceSection: 'AIAG-VDA 2.6.6',
        });
    }

    // Check for high severity safety items
    const highSeveritySafetyItems = pfmeaItems.filter(i => i.severity >= 9);
    if (highSeveritySafetyItems.length > 0) {
        const highSevNoAction = highSeveritySafetyItems.filter(i => i.recommendedActions?.length === 0);
        if (highSevNoAction.length > 0) {
            findings.push({
                id: 'S5-008',
                step: 5,
                category: 'Safety Critical Items',
                requirement: 'Severity 9-10 items MUST have actions regardless of Occurrence/Detection',
                finding: `${highSevNoAction.length} safety-critical items (S≥9) have no recommended actions`,
                severity: 'critical',
                auditorTip: 'CRITICAL: Safety items always need action. This is a potential audit failure.',
                ppapImpact: 'Missing safety actions can result in PPAP rejection',
                recommendation: 'Add recommended actions for ALL Severity 9-10 items',
                referenceSection: 'AIAG-VDA 2.6.7',
            });
        }
    }

    auditorNotes.push('Risk Analysis provides the basis for prioritization. Ratings must be justified.');
    auditorNotes.push('AIAG-VDA uses Action Priority (AP), not just RPN. Ensure AP logic is followed.');
    auditorNotes.push('Severity 9-10 = Safety/Regulatory concern. Always requires action.');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 5,
        stepName: 'Risk Analysis',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// AIAG-VDA STEP 6: OPTIMIZATION AUDIT
// ============================================================

// AIAG-VDA STEP 6: OPTIMIZATION AUDIT
// ============================================================

function auditStep6Optimization(pfmeaItems: PfmeaItem[]): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 4;
    let completedItems = 0;

    // 1. Identification of High Risk Items (Strict Criteria)
    // ----------------------------------------------------------------
    // Users requested strict "Hammering" on SxO, SxD, RPN and Implementation
    const highRiskItems = pfmeaItems.filter(i => {
        // High Action Priority (AIAG-VDA Standard)
        if (i.actionPriority === 'H') return true;

        // High Severity (Safety/Compliance) irrespective of other ratings
        if (i.severity >= 9) return true;

        // High Risk Priority Number (RPN) - Legacy but requested check
        const rpn = i.severity * i.occurrence * i.detection;
        if (rpn > 100) return true;

        // High Severity x Occurrence (Criticality)
        const sxo = i.severity * i.occurrence;
        if (sxo >= 45) return true; // e.g., S=9, O=5 or S=5, O=9

        // High Severity x Detection
        const sxd = i.severity * i.detection;
        if (sxd >= 45) return true;

        return false;
    });

    const mediumRiskItems = pfmeaItems.filter(i => i.actionPriority === 'M' && !highRiskItems.includes(i));
    const allActions = pfmeaItems.flatMap(i => i.recommendedActions || []);

    // Auditor Worksheet Header
    auditorNotes.push('AUDITOR WORKSHEET - STEP 6: OPTIMIZATION');
    auditorNotes.push('--------------------------------------------------');
    auditorNotes.push(`Total Items: ${pfmeaItems.length} | High Risk Items: ${highRiskItems.length}`);

    // CHECK 1: High Risk Mitigation Planning
    // Do all high risk items have at least one action defined?
    // ----------------------------------------------------------------
    if (highRiskItems.length > 0) {
        const unaddressedHighRisk = highRiskItems.filter(i => (i.recommendedActions?.length || 0) === 0);

        if (unaddressedHighRisk.length > 0) {
            findings.push({
                id: 'S6-001',
                step: 6,
                category: 'High Risk Mitigation',
                requirement: 'All High Risk items (AP=H, S≥9, SxO≥45, RPN>100) MUST have risk reduction actions defined',
                finding: `${unaddressedHighRisk.length} High Risk items have NO actions defined.`,
                severity: 'critical',
                auditorTip: 'AUDITOR NOTE: Critical Failure. Zero tolerance for ignored high risks. See Worksheet for Item IDs.',
                ppapImpact: 'PPAP REJECTION: Unaddressed high risks.',
                recommendation: 'Define actions immediately for items: ' + unaddressedHighRisk.map(i => i.processStepNumber).join(', '),
                referenceSection: 'AIAG-VDA 2.7.1',
            });
            // Detailed notes for the "Worksheet"
            unaddressedHighRisk.forEach(i => {
                const rpn = i.severity * i.occurrence * i.detection;
                auditorNotes.push(`[CRITICAL] Item ${i.processStepNumber}: ${i.failureMode} (S=${i.severity}, RPN=${rpn}) - NO ACTION PLANNED`);
            });
        } else {
            completedItems++;
            auditorNotes.push('[PASS] All high risk items have planned actions.');
        }

        // CHECK 2: Implementation Status
        // Are actions for high risk items actually COMPLETED?
        // ----------------------------------------------------------------
        const highRiskWithOpenActions = highRiskItems.filter(i =>
            i.recommendedActions?.length > 0 &&
            !i.recommendedActions.some(a => a.status === 'completed')
        );

        if (highRiskWithOpenActions.length > 0) {
            findings.push({
                id: 'S6-002',
                step: 6,
                category: 'Action Implementation',
                requirement: 'Actions for High Risk items must be fully implemented and verified',
                finding: `${highRiskWithOpenActions.length} High Risk items have actions that are NOT completed.`,
                severity: 'major',
                auditorTip: 'AUDITOR NOTE: "Open" or "In Progress" is not enough for High Risk. Risk is not reduced until action is COMPLETE.',
                ppapImpact: 'Interim Approval Only - Requires Action Plan',
                recommendation: 'Complete implementation for items: ' + highRiskWithOpenActions.map(i => i.processStepNumber).join(', '),
                referenceSection: 'AIAG-VDA 2.7.3',
            });
            highRiskWithOpenActions.forEach(i => {
                auditorNotes.push(`[MAJOR] Item ${i.processStepNumber}: Actions define but not implemented. Risk remains high.`);
            });
        } else {
            completedItems++;
            if (highRiskItems.length > 0) auditorNotes.push('[PASS] All high risk actions are implemented.');
        }

    } else {
        // No high risk items - automatically pass strict checks
        completedItems += 2;
        auditorNotes.push('[INFO] No High Risk items identified based on strict criteria.');
    }

    // CHECK 3: Action Plan Quality (Ownership & Dates)
    // ----------------------------------------------------------------
    if (allActions.length > 0) {
        const incompleteActions = allActions.filter(a => !a.responsiblePerson || !a.targetDate);
        if (incompleteActions.length > 0) {
            findings.push({
                id: 'S6-003',
                step: 6,
                category: 'Action Plan Detail',
                requirement: 'All actions must have clear Ownership and Target Dates',
                finding: `${incompleteActions.length} actions missing Owner or Target Date`,
                severity: 'major',
                auditorTip: 'Auditor Worksheet: Ambiguous responsibility = no action. Assign specific names and dates.',
                recommendation: 'Update action plan with names and dates.',
                referenceSection: 'AIAG-VDA 2.7.2',
            });
        } else {
            completedItems++;
        }

        // CHECK 4: Overdue Actions
        // ----------------------------------------------------------------
        const overdue = allActions.filter(a => a.status === 'open' && a.targetDate && new Date(a.targetDate) < new Date());
        if (overdue.length > 0) {
            findings.push({
                id: 'S6-004',
                step: 6,
                category: 'Project Timing',
                requirement: 'Actions must be completed on time',
                finding: `${overdue.length} actions are OVERDUE`,
                severity: 'major',
                auditorTip: 'AUDITOR NOTE: Overdue actions indicate project management failure.',
                recommendation: 'Reschedule or complete overdue actions immediately.',
                referenceSection: 'AIAG-VDA 2.7.3'
            });
            auditorNotes.push(`[MAJOR] ${overdue.length} actions are past their due date.`);
        } else {
            completedItems++;
        }
    } else {
        // No actions at all
        if (mediumRiskItems.length > 0) {
            findings.push({
                id: 'S6-005',
                step: 6,
                category: 'Continuous Improvement',
                requirement: 'Medium risk items should be considered for improvement',
                finding: 'No actions defined despite having Medium risk items',
                severity: 'minor',
                auditorTip: 'Recommendation: Demonstrate continuous improvement by addressing top Medium risks.',
                recommendation: 'Add actions for top 3 Medium risk items.',
                referenceSection: 'AIAG-VDA 2.7'
            });
            completedItems++; // Giving partial credit since it's only minor
        } else if (highRiskItems.length === 0) {
            completedItems += 2; // Nothing to do
        }
    }

    auditorNotes.push('--------------------------------------------------');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 6,
        stepName: 'Optimization',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// AIAG-VDA STEP 7: RESULTS DOCUMENTATION AUDIT
// ============================================================

function auditStep7Documentation(
    pfmeaItems: PfmeaItem[],
    controlPlans: ControlPlan[],
    processFlow: ProcessFlowDiagram | null
): StepAuditResult {
    const findings: AuditFinding[] = [];
    const auditorNotes: string[] = [];
    const requiredItems = 5;
    let completedItems = 0;

    // Check Control Plan exists and has items
    const controlPlanItems = controlPlans.flatMap(cp => cp.items || []);
    if (controlPlanItems.length === 0) {
        findings.push({
            id: 'S7-001',
            step: 7,
            category: 'Control Plan',
            requirement: 'Control Plan must be generated from PFMEA',
            finding: 'No Control Plan items exist',
            severity: 'critical',
            auditorTip: 'Control Plan is a REQUIRED PPAP element. It must reference PFMEA.',
            ppapImpact: 'PPAP Element #6 - Control Plan is mandatory',
            recommendation: 'Generate Control Plan from PFMEA analysis',
            referenceSection: 'AIAG-VDA 2.8',
        });
    } else {
        completedItems++;

        // Check Control Plan to PFMEA linkage
        const cpItemsLinkedToPfmea = controlPlanItems.filter(i => i.linkedPfmeaId);
        if (cpItemsLinkedToPfmea.length < controlPlanItems.length * 0.8) {
            findings.push({
                id: 'S7-002',
                step: 7,
                category: 'Control Plan Linkage',
                requirement: 'Control Plan items should reference source PFMEA items',
                finding: `Only ${cpItemsLinkedToPfmea.length} of ${controlPlanItems.length} Control Plan items linked to PFMEA`,
                severity: 'major',
                auditorTip: 'Auditors verify: "Which PFMEA item does this control address?"',
                recommendation: 'Link Control Plan items to their source PFMEA items',
                referenceSection: 'AIAG-VDA 2.8.1',
            });
        } else {
            completedItems++;
        }
    }

    // Check Special Characteristics
    const ccItems = pfmeaItems.filter(i => i.specialCharacteristic === 'CC');
    const ccInControlPlan = controlPlanItems.filter(i => i.specialCharacteristic === 'CC');

    if (ccItems.length > 0) {
        if (ccInControlPlan.length < ccItems.length) {
            findings.push({
                id: 'S7-003',
                step: 7,
                category: 'Special Characteristics',
                requirement: 'All Critical Characteristics (CC) must flow to Control Plan',
                finding: `${ccItems.length} CC items in PFMEA but only ${ccInControlPlan.length} in Control Plan`,
                severity: 'critical',
                auditorTip: 'CCs are safety/regulatory critical. They MUST be controlled and documented.',
                ppapImpact: 'Missing CC controls = PPAP rejection',
                recommendation: 'Ensure all Critical Characteristics have corresponding Control Plan entries',
                referenceSection: 'AIAG-VDA 2.8.2',
            });
        } else {
            completedItems++;
        }
    } else {
        // Check if CC/SC analysis was done
        const unclassified = pfmeaItems.filter(i => !i.specialCharacteristic || i.specialCharacteristic === 'none');
        if (unclassified.length === pfmeaItems.length && pfmeaItems.length > 0) {
            findings.push({
                id: 'S7-004',
                step: 7,
                category: 'Special Characteristics',
                requirement: 'PFMEA items should be evaluated for Critical/Significant characteristics',
                finding: 'No items classified as CC or SC - classification not performed',
                severity: 'major',
                auditorTip: 'Auditors ask: "How did you identify special characteristics?"',
                recommendation: 'Review all items and classify CC (Critical) and SC (Significant) characteristics',
                referenceSection: 'AIAG-VDA 2.8.2',
            });
        }
    }

    // Check PFMEA approval status
    const approvedItems = pfmeaItems.filter(i => i.status === 'approved' && i.approvedBy);
    if (approvedItems.length < pfmeaItems.length * 0.8) {
        findings.push({
            id: 'S7-005',
            step: 7,
            category: 'PFMEA Approval',
            requirement: 'PFMEA items should be reviewed and approved',
            finding: `Only ${approvedItems.length} of ${pfmeaItems.length} items are approved`,
            severity: 'major',
            auditorTip: 'Auditors verify sign-off. Unapproved FMEA = incomplete documentation.',
            recommendation: 'Review and approve PFMEA items through workflow',
            referenceSection: 'AIAG-VDA 2.8.3',
        });
    } else {
        completedItems++;
    }

    // Check Process Flow Diagram status
    if (!processFlow || processFlow.status !== 'approved') {
        findings.push({
            id: 'S7-006',
            step: 7,
            category: 'Process Flow Approval',
            requirement: 'Process Flow Diagram must be approved',
            finding: processFlow ? `PFD status is "${processFlow.status}", not approved` : 'No PFD exists',
            severity: 'major',
            auditorTip: 'PFD is a PPAP deliverable. Must be approved before submission.',
            ppapImpact: 'Unapproved PFD = incomplete PPAP',
            recommendation: 'Review and approve Process Flow Diagram',
            referenceSection: 'AIAG-VDA 2.8.4',
        });
    } else {
        completedItems++;
    }

    // Check Work Instructions linkage
    const itemsWithWI = pfmeaItems.filter(i => i.linkedWorkInstructionId);
    if (itemsWithWI.length < pfmeaItems.length * 0.5 && pfmeaItems.length > 0) {
        findings.push({
            id: 'S7-007',
            step: 7,
            category: 'Work Instructions',
            requirement: 'Critical controls should have Work Instructions for operators',
            finding: `Only ${itemsWithWI.length} of ${pfmeaItems.length} items linked to Work Instructions`,
            severity: 'minor',
            auditorTip: 'Best practice: Link PFMEA to Work Instructions to ensure operator awareness.',
            recommendation: 'Create and link Work Instructions for critical operations',
            referenceSection: 'AIAG-VDA 2.8.5',
        });
    }

    auditorNotes.push('Documentation step produces PPAP deliverables: Control Plan, PFD, PFMEA.');
    auditorNotes.push('All documents must be linked, approved, and version controlled.');
    auditorNotes.push('Special Characteristics (CC/SC) must flow consistently through all documents.');

    const score = requiredItems > 0 ? Math.round((completedItems / requiredItems) * 100) : 0;
    const status = calculateStrictStatus(score, findings);

    return {
        step: 7,
        stepName: 'Results Documentation',
        score,
        status,
        findings,
        requiredItems,
        completedItems,
        auditorNotes,
    };
}

// ============================================================
// MAIN AUDIT FUNCTION
// ============================================================

export function runAiagVdaAudit(
    pfmeaItems: PfmeaItem[],
    processFlow: ProcessFlowDiagram | null,
    controlPlans: ControlPlan[],
    projectName: string | null,
    scoping?: ProjectScoping,
    functions?: ProcessFunction[],
    requirements?: ProductRequirement[]
): AuditReport {
    // Run audits for each step
    const step1 = auditStep1Planning(scoping, projectName);
    const step2 = auditStep2Structure(processFlow, pfmeaItems);
    const step3 = auditStep3Function(processFlow, pfmeaItems, functions || [], requirements || []);
    const step4 = auditStep4Failure(pfmeaItems);
    const step5 = auditStep5Risk(pfmeaItems);
    const step6 = auditStep6Optimization(pfmeaItems);
    const step7 = auditStep7Documentation(pfmeaItems, controlPlans, processFlow);

    const stepResults = [step1, step2, step3, step4, step5, step6, step7];

    // Collect all findings by severity
    const allFindings = stepResults.flatMap(s => s.findings);
    const criticalFindings = allFindings.filter(f => f.severity === 'critical');
    const majorFindings = allFindings.filter(f => f.severity === 'major');
    const minorFindings = allFindings.filter(f => f.severity === 'minor');

    // Calculate weighted overall score
    // Steps 4 and 5 (Failure and Risk Analysis) are weighted higher
    const weights = [10, 15, 10, 25, 20, 10, 10]; // Total = 100
    const overallScore = Math.round(
        stepResults.reduce((sum, step, idx) => sum + (step.score * weights[idx] / 100), 0)
    );

    // Calculate PPAP readiness
    const ppapElements = {
        hasProcessFlow: !!processFlow && (processFlow.steps?.length ?? 0) > 0,
        hasPfmea: pfmeaItems.length > 0,
        hasControlPlan: controlPlans.some(cp => cp.items?.length > 0),
        hasSpecialChars: pfmeaItems.some(i => i.specialCharacteristic === 'CC' || i.specialCharacteristic === 'SC'),
    };
    const ppapScore = Object.values(ppapElements).filter(Boolean).length * 25;

    const ppapGaps: string[] = [];
    if (!ppapElements.hasProcessFlow) ppapGaps.push('Missing Process Flow Diagram (PPAP Element #4)');
    if (!ppapElements.hasPfmea) ppapGaps.push('Missing PFMEA (PPAP Element #7)');
    if (!ppapElements.hasControlPlan) ppapGaps.push('Missing Control Plan (PPAP Element #6)');
    if (!ppapElements.hasSpecialChars) ppapGaps.push('No Special Characteristics identified');

    // Generate recommendations
    const recommendations = criticalFindings
        .slice(0, 5)
        .map(f => `[${f.category}] ${f.recommendation}`);

    return {
        overallScore,
        ppapReadiness: ppapScore,
        status: criticalFindings.length === 0 ? (majorFindings.length <= 3 ? 'ready' : 'needs-work') : 'not-ready',
        stepResults,
        criticalFindings,
        majorFindings,
        minorFindings,
        ppapGaps,
        recommendations,
        generatedAt: new Date(),
    };
}

export default runAiagVdaAudit;
